import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSave, FaSearch, FaUndo } from 'react-icons/fa';
import { defineMessages, useIntl } from 'react-intl';
import EmployeeFilterService from '../services/EmployeeFilterService';
import ToastrService from '../services/ToastrService';
import { c } from '../utils/c';
import { dynamicSort } from '../utils/dynamicSort';
import { FilterSaveModal } from './FilterSaveModal';
const messages = defineMessages({
    SaveSearchCriteria: {
        id: 'SaveSearchCriteria',
        defaultMessage: 'Save search criteria',
    },
    RenameSearchCriteria: {
        id: 'RenameSearchCriteria',
        defaultMessage: 'Rename search criteria',
    },
    DeleteSearchCriteria: {
        id: 'DeleteSearchCriteria',
        defaultMessage: 'Delete search criteria',
    },
    SelectSearchCriteria: {
        id: 'SelectSearchCriteria',
        defaultMessage: 'Select search criteria',
    },
    ReplaceSearchCriteria: {
        id: 'ReplaceSearchCriteria',
        defaultMessage: 'Replace search criteria',
    },
    ResetFilters: {
        id: 'ResetFilters',
        defaultMessage: 'Reset filters',
    },
    SelectAll: {
        id: 'SelectAll',
        defaultMessage: 'Select all',
    },
    // Same labels used in AngularJS modal
    SearchCriteriaNameInputTitle: {
        id: 'SearchCriteriaNameInputTitle',
        defaultMessage: 'SearchCriteriaNameInputTitle',
    },
    SearchCriteriaNameInputMessage: {
        id: 'SearchCriteriaNameInputMessage',
        defaultMessage: 'SearchCriteriaNameInputMessage',
    },
    SearchCriteriaNameInputPlaceholder: {
        id: 'SearchCriteriaNameInputPlaceholder',
        defaultMessage: 'SearchCriteriaNameInputPlaceholder',
    },
});
export { messages };
export const FilterSave = ({ hasActiveFilters, screen, filtersObject, resetFilters, setFilter, }) => {
    const intl = useIntl();
    const { formatMessage } = intl;
    const [savedFilters, setSavedFilters] = useState([]);
    const [isNameModalOpen, setIsNameModalOpen] = useState(false);
    const [modalFilter, setModalFilter] = useState();
    const hasSavedFilters = useMemo(() => savedFilters.length > 0, [savedFilters.length]);
    const selectedFilter = useMemo(() => {
        if (!hasSavedFilters) {
            return undefined;
        }
        return savedFilters.find(item => item.json === JSON.stringify(filtersObject));
    }, [filtersObject, hasSavedFilters, savedFilters]);
    const reloadSavedFilters = useCallback(() => {
        setSavedFilters(EmployeeFilterService.getScreenFilters(screen)
            .map(x => (Object.assign(Object.assign({}, x), { filter: JSON.parse(x.json) || {} })))
            .sort(dynamicSort('name')));
    }, [screen]);
    useEffect(() => {
        EmployeeFilterService.load().then(reloadSavedFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const showSaveFilterModal = () => {
        setModalFilter({
            name: '',
            filter: filtersObject,
            screen,
            json: JSON.stringify(filtersObject),
        });
        setIsNameModalOpen(true);
    };
    const selectFilter = (filterId) => {
        const savedFilter = savedFilters.find(item => item.id === filterId);
        if (savedFilter) {
            resetFilters();
            setFilter(savedFilter.filter);
        }
    };
    const replaceFilter = async (filter, event) => {
        event.stopPropagation();
        event.preventDefault();
        if (!filter || !filter.id)
            return;
        const model = Object.assign(Object.assign({}, filter), { json: JSON.stringify(filtersObject) });
        const updatedFilter = await EmployeeFilterService.setFilter(model);
        if (updatedFilter) {
            ToastrService.changesSaved(intl);
            reloadSavedFilters();
        }
        return updatedFilter;
    };
    const showRenameFilterModal = (filter, event) => {
        event.stopPropagation();
        event.preventDefault();
        if (!filter || !filter.id)
            return;
        setModalFilter(filter);
        setIsNameModalOpen(true);
    };
    const deleteFilter = async (filter, event) => {
        event.stopPropagation();
        event.preventDefault();
        if (!filter || !filter.id)
            return;
        await EmployeeFilterService.deleteFilter(filter);
        ToastrService.changesSaved(intl);
        reloadSavedFilters();
    };
    const saveFilter = async () => {
        var _a;
        if (!((_a = modalFilter === null || modalFilter === void 0 ? void 0 : modalFilter.name) === null || _a === void 0 ? void 0 : _a.length))
            return;
        const updatedFilter = await EmployeeFilterService.setFilter(modalFilter);
        if (updatedFilter) {
            ToastrService.changesSaved(intl);
            reloadSavedFilters();
        }
        setIsNameModalOpen(false);
        setModalFilter(undefined);
        return updatedFilter;
    };
    if (!hasSavedFilters && hasActiveFilters) {
        return (_jsxs(_Fragment, { children: [_jsxs("button", Object.assign({ type: "button", className: "btn btn-default", onClick: showSaveFilterModal }, { children: [_jsx(FaSave, { className: "react-icon fa-fw margin5R" }), formatMessage(messages.SaveSearchCriteria)] })), _jsx(FilterSaveModal, { isNameModalOpen: isNameModalOpen, modalFilter: modalFilter, saveFilter: saveFilter, setModalFilter: setModalFilter, setIsNameModalOpen: setIsNameModalOpen })] }));
    }
    if (hasSavedFilters) {
        return (_jsxs("div", Object.assign({ className: "btn-group" }, { children: [_jsx(FilterSaveModal, { isNameModalOpen: isNameModalOpen, modalFilter: modalFilter, saveFilter: saveFilter, setModalFilter: setModalFilter, setIsNameModalOpen: setIsNameModalOpen }), _jsxs("button", Object.assign({ type: "button", className: "btn btn-default dropdown-toggle", "data-toggle": "dropdown", "aria-haspopup": "true", "aria-expanded": "false" }, { children: [_jsx(FaSearch, { className: c('react-icon margin5R', selectedFilter ? 'text-success' : 'text-orange') }), selectedFilter && selectedFilter.name
                            ? selectedFilter.name
                            : formatMessage(messages.SelectSearchCriteria), _jsx("span", { className: "caret" })] })), _jsxs("ul", Object.assign({ className: "dropdown-menu" }, { children: [hasActiveFilters && !selectedFilter && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: showSaveFilterModal }, { children: [_jsx(FaSave, { className: "react-icon fa-fw margin5R" }), formatMessage(messages.SaveSearchCriteria)] })) })), hasActiveFilters && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: resetFilters }, { children: [_jsx(FaUndo, { className: "react-icon fa-fw margin5R" }), formatMessage(messages.ResetFilters)] })) })), hasActiveFilters && _jsx("li", { role: "separator", className: "divider" }), hasSavedFilters &&
                            savedFilters.map(filter => (_jsx("li", Object.assign({ className: c(selectedFilter && selectedFilter.id === filter.id && 'active') }, { children: _jsxs("a", Object.assign({ onClick: () => selectFilter(filter.id), id: filter.id }, { children: [_jsx("span", { children: filter.name }), !selectedFilter && hasActiveFilters && (_jsx("i", { className: "fa fa-exchange clickable", title: formatMessage(messages.ReplaceSearchCriteria), onClick: e => replaceFilter(filter, e) })), _jsx("i", { className: "fa fa-pencil clickable", title: formatMessage(messages.RenameSearchCriteria), onClick: e => showRenameFilterModal(filter, e) }), _jsx("i", { className: "fa fa-trash clickable", title: formatMessage(messages.DeleteSearchCriteria), onClick: e => deleteFilter(filter, e) })] })) }), filter.id)))] }))] })));
    }
    return null;
};
